import { ActionIcon, Box, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconArrowsDiagonalMinimize2 } from '@tabler/icons-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CONTRACT_STUDIO_TEMPLATE_ADDRESS, explorePageActive } from '@/constants/blockchain'
import { ipfsDomains, shortenHash } from '@/utils/utils'

import { BodyStack } from '../../styled/components'
import RootButton from '../Buttons/RootButton'
import Loading from '../Loading'
import ImageWrapper, { SimpleImage } from '../MediaWrappers/ImageWrapper'
import IpfsImageLoader from '../MediaWrappers/IpfsImageLoader'
import ModalSkeleton from '../Modals/ModalSkeleton'
import TooltipTitle from '../TooltipTitle'
import classes from './DetailsPage.module.css'

export const DETAILS_PAGE_IMAGE_SIZE = 750
export const DETAILS_PAGE_IMAGE_SIZE_MOBILE = 330

interface ActionButton {
  text: string
  onClick: () => void
  complementaryText?: string
}

interface Price {
  amount: string
  currency: string
}

interface Description {
  text: string | undefined
  tooltip?: string
}
interface DetailsPageProps {
  image: string | undefined
  title: string | undefined
  collectionId?: string
  collectionName?: string
  description: Description
  optionsMenu?: React.ReactNode
  tags?: string[]
  badge?: React.ReactNode
  price?: Price
  actionButton?: ActionButton
  loading?: boolean
  itemHistory?: React.ReactNode
  children: React.ReactNode
}

const DetailsPage: React.FC<DetailsPageProps> = ({
  image,
  title,
  collectionId,
  collectionName,
  description,
  tags,
  badge,
  price,
  optionsMenu,
  actionButton = undefined,
  loading = false,
  children,
  itemHistory = null,
}) => {
  const theme = useMantineTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isDown910 = useMediaQuery(`(max-width: 910px)`)
  const [openedFullImage, { open: openFullImage, close: closeFullImage }] = useDisclosure(false)
  const { t } = useTranslation()

  // Confirm if collection is from Studio Template
  const studioTemplateCollection = useMemo(
    () =>
      collectionId?.toLowerCase() === CONTRACT_STUDIO_TEMPLATE_ADDRESS.toLowerCase()
        ? 'Artwork Template'
        : '',
    [collectionId]
  )

  const processedTags = useMemo(
    () => (tags ?? []).map(tag => (tag.includes('#') ? tag : `#${tag}`)),
    [tags]
  )
  const titleAndButton = useMemo(
    () =>
      title && (
        <Stack gap="0.5em">
          <Title order={isMobile ? 3 : 2} style={{ textAlign: 'left', wordWrap: 'break-word' }}>
            {title}
          </Title>
          {collectionId && (
            <Stack style={{ gap: '0.5em', flexDirection: 'row', alignItems: 'baseline' }}>
              <Text
                style={{
                  color: theme.colors.textAndIcon[0],
                  fontSize: '12px',
                  textAlign: 'left',
                }}
              >
                from
              </Text>
              <Text
                className={classes.collectionLink}
                style={{
                  color: theme.colors.l1Primary[0],
                }}
                onClick={() => navigate(`/collection/${collectionId}`)}
              >
                {studioTemplateCollection || collectionName || shortenHash(collectionId)}
              </Text>
            </Stack>
          )}
          {badge}
          {actionButton && (
            <Stack
              style={{
                flexDirection: 'row',
                gap: '30px',
                alignItems: 'center',
                marginTop: '10px',
              }}
            >
              {actionButton.complementaryText && (
                <Title order={4}>{actionButton.complementaryText}</Title>
              )}
              <RootButton expand onClick={actionButton.onClick}>
                {actionButton.text}
              </RootButton>
            </Stack>
          )}
        </Stack>
      ),
    [title, actionButton]
  )

  const infoColumn = useMemo(
    () => (
      <Stack gap="30px" style={{ minWidth: '350px', width: '100%' }}>
        {titleAndButton}
        {/* DESCRIPTION & TAGS */}
        <Stack gap="10px">
          <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <TooltipTitle
              order={4}
              title={t('components.detailsPage.description', 'Description')}
              tooltip={description.tooltip}
            />
            {optionsMenu}
          </Stack>
          <Stack gap="10px">
            <Text>{description.text}</Text>
            {processedTags.length > 0 && (
              <>
                {explorePageActive ? (
                  <Stack
                    className={classes.descriptionTags}
                    style={{ flexDirection: 'row', flexWrap: 'wrap', gap: '5px' }}
                  >
                    {processedTags.map(tag => (
                      <Text
                        key={tag}
                        component="span"
                        onClick={() => navigate(`/explore/items?search=${encodeURIComponent(tag)}`)}
                        className={classes.collectionLink}
                      >
                        {tag}
                      </Text>
                    ))}
                  </Stack>
                ) : (
                  <Text className={classes.descriptionTags}>{processedTags.join(' ')}</Text>
                )}
              </>
            )}
          </Stack>
        </Stack>
        {/* FOR SALE */}
        {price && (
          <Stack gap="0px">
            <Title order={4}>{t('components.detailsPage.forSale', 'For Sale')}</Title>
            <Text>{t('components.detailsPage.price', 'Price') as string}</Text>
            <Title order={2}>
              {price.amount} {price.currency}
            </Title>
          </Stack>
        )}
        {children}
      </Stack>
    ),
    [description, optionsMenu, processedTags, price, children, titleAndButton]
  )

  // Image needs special handling because it will scale-down (cannot use ImageWrapper)
  const img = useMemo(() => {
    if (!image) return null

    const isIpfsImage =
      (image && ipfsDomains.some(domain => image.startsWith(domain))) ||
      image?.startsWith('ipfs://')

    const src = isIpfsImage ? image.replace('ipfs://', '') : image
    const alt = title
    const style = {
      width: '100%',
      height: '100%',
      objectFit: 'scale-down',
    } as React.CSSProperties

    return isIpfsImage ? (
      <IpfsImageLoader ipfsHash={src} alt={alt} style={style} />
    ) : (
      <SimpleImage src={src} alt={alt} style={style} />
    )
  }, [image])

  const imgBox = useMemo(
    () => (
      <Box
        className={classes.imgBox}
        style={{
          width: '100%',
          height: 'calc(100vh - 120px)',
          maxHeight: isDown910 ? '450px' : '750px',
        }}
        onClick={openFullImage}
      >
        {img}
        {/* <ActionIcon
          onClick={openFullImage}
          className={classes.fullScreenIcon}
          style={{
            backgroundColor: theme.colors.background2[0],
          }}
        >
          <IconArrowsDiagonal />
        </ActionIcon> */}
      </Box>
    ),
    [isMobile, image, openFullImage]
  )

  return (
    <BodyStack>
      <>
        {loading ? (
          <Loading />
        ) : (
          <>
            {image && (
              <Stack
                style={{
                  display: 'flex',
                  flexDirection: isDown910 ? 'column' : 'row',
                  justifyContent: 'space-between',
                  gap: '40px',
                  marginTop: '30px',
                  marginBottom: '60px',
                }}
              >
                {imgBox}
                <Box
                  style={{
                    maxWidth: isDown910 ? '100%' : '350px',
                  }}
                >
                  {infoColumn}
                </Box>
              </Stack>
            )}
            {itemHistory}
          </>
        )}
      </>
      <ModalSkeleton fullScreen opened={openedFullImage} onClose={closeFullImage}>
        <Stack
          style={{ cursor: 'zoom-out', justifyContent: 'center', alignItems: 'center' }}
          onClick={closeFullImage}
        >
          <ActionIcon onClick={closeFullImage} className={classes.fullScreenIcon}>
            <IconArrowsDiagonalMinimize2 />
          </ActionIcon>
          <ImageWrapper
            src={image}
            style={{
              height: '100vh',
              width: '100vw',
              objectFit: 'contain',
            }}
            alt="details-page"
          />
        </Stack>
      </ModalSkeleton>
    </BodyStack>
  )
}

export default DetailsPage
